/**
 * EditorHeader — top header bar with brand, backend status, and user info.
 * Extracted from App.tsx — pure structural refactor.
 */

import { useState } from "react";

import {
  selectCurrentUser,
  useEditorStore,
} from "../state/editorStore";
import { useConnectionStore } from "../state/connectionStore";
import { useT } from "../i18n";
import { AgentBridgeToggle } from "./AgentBridgeToggle";
import { ConnectDialog } from "./ConnectDialog";

export type BackendRuntimeState = {
  status: "loading" | "ready" | "error";
  apiBaseUrl: string;
  serverAlias?: string;
  authMode?: string;
  deploymentMode?: string;
  authenticated?: boolean;
  sessionLabel?: string;
  sessionDescription?: string;
  notice?: string;
  signInHref?: string | null;
  signInLabel?: string | null;
  adminUserCount?: number;
  errorMessage?: string;
};

export function EditorHeader({
  backendRuntime,
}: {
  backendRuntime: BackendRuntimeState;
}) {
  const t = useT();
  const state = useEditorStore((store) => store.state);
  const currentUser = selectCurrentUser(state);
  // ABB-S7c — the live 5-state agent-bridge connection status drives the header
  // pill; the Connect dialog (a fixed-overlay modal, position:fixed escapes the
  // header layout) is mounted from here and toggled by the adjacent button.
  const connectionStatus = useConnectionStore((s) => s.status);
  const [connectOpen, setConnectOpen] = useState(false);

  return (
    <header className="hero-card editor-header" data-testid="hero-card">
      <div className="header-actions">
        <span className="pill pill-strong">
          {t("header.backend", {
            alias:
              backendRuntime.status === "ready"
                ? `${backendRuntime.serverAlias} / ${backendRuntime.deploymentMode}`
                : backendRuntime.status === "error"
                  ? t("backend.offline_fallback")
                  : t("backend.loading_label"),
          })}
        </span>
        <span className="pill">
          {t("header.preview_user", { email: currentUser.email })}
        </span>
        <span
          className={`pill ${currentUser.status === "active" ? "pill-ok" : "pill-warn"}`}
        >
          {t("header.role", {
            role: `${currentUser.role} / ${currentUser.status}`,
          })}
        </span>
        {!backendRuntime.authenticated ? (
          <span className="pill">{t("header.auth_disabled")}</span>
        ) : null}
        <span
          className={`pill pill-connection-${connectionStatus}`}
          data-testid="agent-connect-pill"
        >
          {t(`agent_connect.pill.${connectionStatus}`)}
        </span>
        {/* ABB enable-toggle — the persisted default-OFF enable switch + consent
            copy. It drives the EXISTING connection store connect/disconnect and
            opens the ConnectDialog (mounted below) for the token-pairing step;
            the standalone "Агент" open-button moved INTO the toggle. */}
        <AgentBridgeToggle onRequestConnect={() => setConnectOpen(true)} />
      </div>
      <ConnectDialog open={connectOpen} onClose={() => setConnectOpen(false)} />
    </header>
  );
}
